import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import aiChatbotsService, {
  AIChatbot,
  AIChatbotCreate,
  AIChatbotUpdate,
  AIChatbotFilters,
  TokenUsage,
} from "./aiChatbotsService";

interface AIChatbotsState {
  chatbots: AIChatbot[];
  selectedChatbot: AIChatbot | null;
  tokenUsage: TokenUsage[];
  conversations: any[];
  conversationMessages: any[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  limit: number;
  filters: AIChatbotFilters;
  viewMode: "list" | "grid";
}

const initialState: AIChatbotsState = {
  chatbots: [],
  selectedChatbot: null,
  tokenUsage: [],
  conversations: [],
  conversationMessages: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  page: 1,
  limit: 10,
  filters: {},
  viewMode: "grid",
};

// Async thunks
export const fetchChatbots = createAsyncThunk(
  "aiChatbots/fetchChatbots",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await aiChatbotsService.getChatbots(params);

      // API doğrudan dizi döndürdüğünde, bunu beklenen formata dönüştür
      if (Array.isArray(response)) {
        return {
          chatbots: response,
          total: response.length
        };
      }

      // Eğer API zaten doğru formatta dönüyorsa, direkt kullan
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch chatbots');
    }
  }
);

export const fetchChatbotById = createAsyncThunk(
  "aiChatbots/fetchById",
  async (id: number, thunkAPI) => {
    try {
      return await aiChatbotsService.getChatbot(id);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createChatbot = createAsyncThunk(
  "aiChatbots/create",
  async (chatbotData: AIChatbotCreate, thunkAPI) => {
    try {
      return await aiChatbotsService.createChatbot(chatbotData);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateChatbot = createAsyncThunk(
  "aiChatbots/update",
  async ({ id, chatbotData }: { id: number; chatbotData: AIChatbotUpdate }, thunkAPI) => {
    try {
      return await aiChatbotsService.updateChatbot(id, chatbotData);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteChatbot = createAsyncThunk(
  "aiChatbots/deleteChatbot",
  async (id: number, { rejectWithValue }) => {
    try {
      await aiChatbotsService.deleteChatbot(id);
      return { success: true, id };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to delete chatbot');
    }
  }
);

export const fetchTokenUsage = createAsyncThunk(
  "aiChatbots/fetchTokenUsage",
  async (params: any, { rejectWithValue }) => {
    try {
      return await aiChatbotsService.getTokenUsage(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch token usage');
    }
  }
);

export const sendChatbotMessage = createAsyncThunk(
  'aiChatbots/sendMessage',
  async ({ chatbotId, message }: { chatbotId: number, message: string }, { rejectWithValue }) => {
    try {
      return await aiChatbotsService.sendMessage(chatbotId, message);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchConversationsList = createAsyncThunk(
  'aiChatbots/fetchConversationsList',
  async (params: any, { rejectWithValue }) => {
    try {
      return await aiChatbotsService.getConversationsList(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch conversations');
    }
  }
);

export const fetchChatbotConversations = createAsyncThunk(
  'aiChatbots/fetchChatbotConversations',
  async ({ chatbotId, params }: { chatbotId: number, params: any }, { rejectWithValue }) => {
    try {
      return await aiChatbotsService.getChatbotConversations(chatbotId, params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch chatbot conversations');
    }
  }
);

export const fetchConversationMessages = createAsyncThunk(
  'aiChatbots/fetchConversationMessages',
  async ({ conversationId, params }: { conversationId: number, params: any }, { rejectWithValue }) => {
    try {
      return await aiChatbotsService.getConversationMessages(conversationId, params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch conversation messages');
    }
  }
);

// Slice
const aiChatbotsSlice = createSlice({
  name: "aiChatbots",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setFilters: (state, action: PayloadAction<AIChatbotFilters>) => {
      state.filters = action.payload;
      state.page = 1; // Reset page when filters change
    },
    clearError: (state) => {
      state.error = null;
    },
    setViewMode: (state, action: PayloadAction<"list" | "grid">) => {
      state.viewMode = action.payload;
    },
    setSelectedChatbot: (state, action: PayloadAction<AIChatbot | null>) => {
      state.selectedChatbot = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Chatbots
      .addCase(fetchChatbots.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChatbots.fulfilled, (state, action) => {
        state.isLoading = false;

        let chatbotsList = [];
        let total = 0;

        if (action.payload.data && action.payload.pagination) {
          // New Paginated Response
          chatbotsList = action.payload.data;
          total = action.payload.pagination.total_count;
        } else if (action.payload.chatbots) {
          // Old/Fallback Response
          chatbotsList = action.payload.chatbots;
          total = action.payload.total;
        } else if (Array.isArray(action.payload)) {
          // Direct array response (should be handled by service but safety check)
          chatbotsList = action.payload;
          total = action.payload.length;
        }

        // Sort by updated_at DESC
        state.chatbots = [...chatbotsList].sort((a, b) => {
          const dateA = new Date(a.updated_at).getTime();
          const dateB = new Date(b.updated_at).getTime();
          return dateB - dateA;
        });
        state.totalCount = total;
      })
      .addCase(fetchChatbots.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Chatbot By Id
      .addCase(fetchChatbotById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChatbotById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedChatbot = action.payload;
      })
      .addCase(fetchChatbotById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create Chatbot
      .addCase(createChatbot.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createChatbot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chatbots.push(action.payload);
        state.totalCount = state.chatbots.length;
      })
      .addCase(createChatbot.rejected, (state, action) => {
        state.isLoading = false;
        const payload: any = action.payload;
        state.error = typeof payload === 'string' ? payload : (Array.isArray(payload) ? payload.map((e) => e?.msg || JSON.stringify(e)).join(', ') : (payload?.msg || JSON.stringify(payload)));
      })

      // Update Chatbot
      .addCase(updateChatbot.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateChatbot.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.chatbots.findIndex(
          (chatbot) => chatbot.id === action.payload.id
        );
        if (index !== -1) {
          state.chatbots[index] = action.payload;
        }
        if (state.selectedChatbot?.id === action.payload.id) {
          state.selectedChatbot = action.payload;
        }
      })
      .addCase(updateChatbot.rejected, (state, action) => {
        state.isLoading = false;
        const payload: any = action.payload;
        state.error = typeof payload === 'string' ? payload : (Array.isArray(payload) ? payload.map((e) => e?.msg || JSON.stringify(e)).join(', ') : (payload?.msg || JSON.stringify(payload)));
      })

      // Delete Chatbot
      .addCase(deleteChatbot.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteChatbot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chatbots = state.chatbots.filter((chatbot) => chatbot.id !== action.payload.id);
        state.totalCount -= 1;
        if (state.selectedChatbot?.id === action.payload.id) {
          state.selectedChatbot = null;
        }
      })
      .addCase(deleteChatbot.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Token Usage
      .addCase(fetchTokenUsage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTokenUsage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tokenUsage = action.payload;
      })
      .addCase(fetchTokenUsage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Send Chatbot Message
      .addCase(sendChatbotMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendChatbotMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendChatbotMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Mesaj gönderilirken bir hata oluştu';
      })

      // Fetch Conversations List
      .addCase(fetchConversationsList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversationsList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data && action.payload.pagination) {
          state.conversations = action.payload.data;
          state.totalCount = action.payload.pagination.total_count;
          state.page = action.payload.pagination.current_page;
        } else {
          state.conversations = action.payload;
          state.totalCount = action.payload.length;
        }
      })
      .addCase(fetchConversationsList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Chatbot Conversations
      .addCase(fetchChatbotConversations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChatbotConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchChatbotConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Conversation Messages
      .addCase(fetchConversationMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversationMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversationMessages = action.payload;
      })
      .addCase(fetchConversationMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPage, setLimit, setFilters, clearError, setViewMode, setSelectedChatbot } = aiChatbotsSlice.actions;
export default aiChatbotsSlice.reducer;